import { createClient } from '@supabase/supabase-js';
import toast from 'react-hot-toast';

// Supabase project credentials
const supabaseUrl = 'https://nupfpfbmendhkndrzqip.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im51cGZwZmJtZW5kaGtuZHJ6cWlwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQxMjM2OTYsImV4cCI6MjA4OTY5OTY5Nn0.mysuAKTSwsrbZbXrh1PNM57BhpB-igtsJVCbDtxgo9s';

// Create Supabase client with better error handling
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Test Supabase connection
export const testSupabaseConnection = async () => {
  try {
    // Test with a simple query that should work
    const { error } = await supabase.from('users').select('count').limit(1);
    if (error) {
      console.warn('Supabase connection test failed:', error.message);
      return false;
    }
    console.log('Supabase connection successful');
    return true;
  } catch (error) {
    console.warn('Supabase connection test failed:', error);
    return false;
  }
};

// Check if we're in offline mode
export const isOfflineMode = () => {
  return !navigator.onLine || window.location.hostname === 'localhost';
};

// Enhanced error handler for network issues
export const handleNetworkError = (error: any) => {
  if (error.message?.includes('Failed to fetch') || 
      error.message?.includes('ERR_NAME_NOT_RESOLVED') ||
      error.message?.includes('NetworkError')) {
    console.warn('Supabase connection failed, using offline mode');
    return true; // Indicates we should use offline mode
  }
  return false;
};

// Database table names (equivalent to Firestore collections)
export const tables = {
  users: 'users',
  projects: 'projects',
  tasks: 'tasks',
  pomodoros: 'pomodoros',
  goals: 'goals',
  friends: 'friends',
  friendRequests: 'friend_requests',
  achievements: 'achievements',
  settings: 'settings',
  userStats: 'user_stats',
  weeklyStats: 'weekly_stats',
  challenges: 'challenges',
  notifications: 'notifications',
  favoriteQuotes: 'favorite_quotes',
  // Community tables
  communityEvents: 'community_events',
  communityTips: 'community_tips',
  communityStories: 'community_stories',
  communityComments: 'community_comments',
  communityLikes: 'community_likes',
  badges: 'badges',
  userBadges: 'user_badges',
  discounts: 'discounts',
  userPoints: 'user_points',
  eventParticipants: 'event_participants'
} as const;

export const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error);
  
  // Check for network errors first
  if (handleNetworkError(error)) {
    console.warn('Network error detected, app will work in offline mode');
    return; // Don't show toast for network errors, just log them
  }
  
  if (error.code === 'PGRST301') {
    toast.error('You do not have permission to perform this action');
  } else if (error.code === 'PGRST116') {
    toast.error('The requested resource was not found');
  } else if (error.code === '23505') {
    toast.error('This resource already exists');
  } else if (error.message?.includes('Failed to fetch')) {
    console.warn('Connection issue, working in offline mode');
  } else {
    toast.error('An error occurred. Please try again.');
  }
  
  throw error;
};

// Helper function to get current timestamp
export const getCurrentTimestamp = () => new Date().toISOString();
